import { Card, CardContent, CardHeader, CardTitle, Input, ScrollArea, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { SaveButton } from '@/shared/ui';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { EmployeesList } from '../EmployeesList/EmployeesList';
import { EditableGroupTitle } from './EditableGroupTitle';
import { EmployeeGroupFormValues } from './types';

type Props = {
  isCreating: boolean;
  groupId?: number;
  isLoadingSaving: boolean;
  onSave: (values: EmployeeGroupFormValues) => void;
  onDelete: () => void;
  onUpdateName?: (name: string) => void;
};

/**
 * Презентер формы группы операторов: название, описание, внешний ID,
 * список входящих операторов (только в режиме редактирования).
 */
export const EmployeeGroupForm: FC<Props> = ({
  isCreating,
  groupId,
  isLoadingSaving,
  onSave,
  onDelete,
  onUpdateName,
}) => {
  const { t } = useTranslation(['common', 'employees']);
  const { handleSubmit, control } = useFormContext<EmployeeGroupFormValues>();
  const { isDirty } = useFormState({ control });
  const canAdmin = useHasPermission('ADMIN_EMPLOYEE_GROUP');

  const onSubmit = handleSubmit(onSave);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center gap-5 border-b">
        {isCreating ? (
          <CardTitle>{t('employees:groups.newGroup')}</CardTitle>
        ) : (
          <EditableGroupTitle isCreate={isCreating} onUpdateName={onUpdateName} disabled={!canAdmin} />
        )}
        {canAdmin && (
          <SaveButton
            className="ml-auto"
            isDirty={isDirty}
            isLoading={isLoadingSaving}
            onClick={onSubmit}
            secondaryAction={
              !isCreating && (
                <ConfirmationButton
                  label={t('employees:groups.deleteGroup')}
                  onConfirm={onDelete}
                  title={t('employees:groups.deleteGroupTitle')}
                  description={t('employees:groups.deleteGroupDescription')}
                />
              )
            }
          />
        )}
      </CardHeader>
      <CardContent className="overflow-hidden flex flex-col gap-6 p-0">
        <ScrollArea className="h-full">
          <div className="flex flex-col gap-6 p-6">
            {isCreating && (
              <Input
                control={control}
                name="name"
                required
                label={t('employees:groups.name')}
                placeholder={t('employees:groups.newGroup')}
                rules={{ required: { value: true, message: t('common:required') } }}
                className="max-w-md"
                disabled={!canAdmin}
              />
            )}
            <Textarea
              control={control}
              name="description"
              label={t('employees:groups.description')}
              rows={3}
              disabled={!canAdmin}
            />
            <Input
              control={control}
              name="externalId"
              label={t('employees:groups.externalId')}
              className="max-w-md"
              disabled={!canAdmin}
            />

            {!isCreating && groupId !== undefined && <EmployeesList groupId={groupId} disabled={!canAdmin} />}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
